import { Button, Dialog, Field, Link, TextOutput } from 'Components/Core';
import { IDialogProps } from 'Components/Core/Dialog/types';
import { actions } from 'Data/ReduxViewModels/Barcode';
import { WorkflowType } from 'Data/ReduxViewModels/Barcode/types';
import { hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const BarcodeProcessingDialog: React.FC<IDialogProps> = () => {
  const dispatch = useDispatch();
  const barcode = useSelector((state) => state.barcode.barcode);
  const resourceId = useSelector((state) => state.barcode.activeWorkflow?.ResourceId);
  const invalidBarcode = useSelector(
    pipe(
      (state) => state.barcode.activeWorkflow?.WorkflowType,
      (workflowType) => workflowType === WorkflowType.Invalid
    )
  ) as boolean;
  const message = useSelector((state) => state.barcode.responseMessage);
  const isSyncing = useSelector((state) => state.barcode.isSyncing);
  const isBarcodePanelOpen = useSelector((state) => state.barcode.isBarcodePanelOpen);
  const [showDialog, setShowDialog] = useState(false);
  const buttonRef = useRef<any>(null);

  useEffect(() => {
    /**
     * Dialog should appear when one of the following is true:
     *    A barcode has been scanned and is currently being evaluated by QN
     *    Barcode type is invalid
     */
    if (isSyncing || invalidBarcode) {
      setShowDialog(true);
      return;
    }

    setShowDialog(false);
  }, [isSyncing, invalidBarcode, barcode]);

  /**
   * The dialog is first shown while the barcode is processing. During this time the button is disabled which means the autofocus trap
   * does not focus on the button. As a result we need to explicitly re-focus on the button when the error is shown.
   */
  useEffect(() => {
    if (hasValue(buttonRef.current) && showDialog && invalidBarcode) {
      buttonRef.current.focus();
    }
  }, [showDialog, invalidBarcode]);

  const closeDialog = () => {
    setShowDialog(false);
    dispatch(actions.barcodeClear());
  };

  const capitaliseMessageType = (messageType?: string) => {
    if (messageType !== null && messageType !== undefined)
      return messageType?.charAt(0).toUpperCase() + messageType?.slice(1);
  };

  return (
    <Dialog
      title="Scanning Barcode"
      width={500}
      visible={!isBarcodePanelOpen && showDialog}
      buttons={[
        <Button focusRef={buttonRef} key="close" disabled={isSyncing} onClick={closeDialog}>
          Close
        </Button>,
      ]}
    >
      {isSyncing && <Field input={<TextOutput />} value="Please wait while the barcode is processing..." />}
      {invalidBarcode && (
        <>
          <Field input={<TextOutput />} value={`Barcode: ${barcode}`} />
          <Field
            input={<TextOutput />}
            severity={message?.MessageType}
            value={`${capitaliseMessageType(message?.MessageType)}: ${message?.Text} `}
            key={message?.Text}
          />
          {message?.MessageType === 'warning' && (
            <Link to={`/equipment/${resourceId}`} onClick={closeDialog}>
              Remove Association on Equipment Page
            </Link>
          )}
        </>
      )}
    </Dialog>
  );
};

export { BarcodeProcessingDialog };
export default BarcodeProcessingDialog;
